/**
 * Apple Pay API Transport Layer
 *
 * Apple Pay shares the same checkout flow as credit cards via /ecommerce/order-checkout.
 * This file only contains Apple Pay-specific endpoints (merchant validation).
 *
 * Checkout flow:
 * - Saved card: apiService.checkoutOrder({ payment_instrument_id, browser_information })
 * - Apple Pay: apiService.checkoutOrder({ single_use_payment_token, browser_information })
 *   (browser_information required for iOS; uses device info from getNativeBrowserData())
 */

import type {ApplePayMerchantSessionData} from '../components/payments/till/PaymentMethodSelector/applePay/types';

import apiService from './apiService';

// ============================================
// TYPES
// ============================================

export interface ValidateMerchantRequest {
  /** From Apple Pay JS onvalidatemerchant event */
  validationURL: string;
  /** Your domain (e.g., "bottlemart.com.au") */
  initiativeContext: string;
}

export interface ValidateMerchantResponse {
  merchantSession: ApplePayMerchantSessionData;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Validate merchant with Apple Pay servers.
 * Backend (middleware) proxies to Apple Pay servers using merchant certificate.
 *
 * Route: POST /apple-pay/validate-merchant
 *
 * Flow:
 * Frontend → Middleware → Apple Pay servers
 *                ↑
 *         MW has merchant cert
 */
export async function validateMerchant(
  request: ValidateMerchantRequest,
): Promise<ValidateMerchantResponse> {
  const response = await apiService
    .getApi()
    .post<ValidateMerchantResponse>('/apple-pay/validate-merchant', request);
  return response.data;
}
